import { ApiService, ApiResponse } from './api';
import { User, Purchase } from '../types';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
}

export interface UserProfile {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  joinDate: string;
  totalPurchases: number;
  totalSpent: number;
  bio?: string;
  website?: string;
  socialLinks?: {
    twitter?: string;
    instagram?: string;
    behance?: string;
    dribbble?: string;
  };
  preferences?: {
    emailNotifications: boolean;
    marketingEmails: boolean;
    theme: 'light' | 'dark' | 'auto';
  };
}

export interface UpdateProfileData {
  name?: string;
  bio?: string;
  website?: string;
  socialLinks?: {
    twitter?: string;
    instagram?: string;
    behance?: string;
    dribbble?: string;
  };
  preferences?: {
    emailNotifications?: boolean;
    marketingEmails?: boolean;
    theme?: 'light' | 'dark' | 'auto';
  };
}

export interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export interface AuthResponse {
  user: UserProfile;
  token: string;
  refreshToken: string;
}

export interface UserStats {
  totalPurchases: number;
  totalSpent: number;
  totalDownloads: number;
  favoriteProducts: number;
  reviewsWritten: number;
  averageRating: number;
}

export interface UserActivity {
  id: string;
  type: 'purchase' | 'download' | 'review' | 'favorite';
  description: string;
  timestamp: string;
  productId?: string;
  productName?: string;
}

export class UserService extends ApiService {
  // Authentication methods
  async login(credentials: LoginCredentials): Promise<ApiResponse<AuthResponse>> {
    const response = await this.post<AuthResponse>('/auth/login', credentials);
    if (response.success && response.data && response.data.token) {
      localStorage.setItem('authToken', response.data.token);
      localStorage.setItem('refreshToken', response.data.refreshToken);
    }
    return response;
  }

  async register(data: RegisterData): Promise<ApiResponse<AuthResponse>> {
    const response = await this.post<AuthResponse>('/auth/register', data);
    if (response.success && response.data && response.data.token) {
      localStorage.setItem('authToken', response.data.token);
      localStorage.setItem('refreshToken', response.data.refreshToken);
    }
    return response;
  }

  async logout(): Promise<ApiResponse<void>> {
    try {
      await this.post<void>('/auth/logout');
    } finally {
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
    }
    return { success: true, data: undefined };
  }

  async refreshToken(): Promise<ApiResponse<{ token: string; refreshToken: string }>> {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await this.post<{ token: string; refreshToken: string }>('/auth/refresh', {
      refreshToken
    });

    if (response.success && response.data) {
      localStorage.setItem('authToken', response.data.token);
      localStorage.setItem('refreshToken', response.data.refreshToken);
    }

    return response;
  }

  async forgotPassword(email: string): Promise<ApiResponse<void>> {
    return this.post<void>('/auth/forgot-password', { email });
  }

  async resetPassword(token: string, newPassword: string): Promise<ApiResponse<void>> {
    return this.post<void>('/auth/reset-password', { token, newPassword });
  }

  async verifyEmail(token: string): Promise<ApiResponse<void>> {
    return this.post<void>('/auth/verify-email', { token });
  }

  async resendVerificationEmail(): Promise<ApiResponse<void>> {
    return this.post<void>('/auth/resend-verification');
  }

  // User profile methods
  async getCurrentUser(): Promise<ApiResponse<UserProfile>> {
    return this.get<UserProfile>('/user/profile');
  }

  async updateProfile(data: UpdateProfileData): Promise<ApiResponse<UserProfile>> {
    return this.put<UserProfile>('/user/profile', data);
  }

  async changePassword(data: ChangePasswordData): Promise<ApiResponse<void>> {
    return this.post<void>('/user/change-password', data);
  }

  async uploadAvatar(file: File, onProgress?: (progress: number) => void): Promise<ApiResponse<{ avatarUrl: string }>> {
    return this.upload<{ avatarUrl: string }>('/user/avatar', file, onProgress);
  }

  async deleteAvatar(): Promise<ApiResponse<void>> {
    return this.delete<void>('/user/avatar');
  }

  // User statistics and activity
  async getUserStats(): Promise<ApiResponse<UserStats>> {
    return this.get<UserStats>('/user/stats');
  }

  async getUserActivity(page: number = 1, limit: number = 20): Promise<ApiResponse<{
    activities: UserActivity[];
    total: number;
    page: number;
    totalPages: number;
  }>> {
    return this.get<{
      activities: UserActivity[];
      total: number;
      page: number;
      totalPages: number;
    }>('/user/activity', { page, limit });
  }

  // Purchase history
  async getPurchaseHistory(page: number = 1, limit: number = 20): Promise<ApiResponse<{
    purchases: Purchase[];
    total: number;
    page: number;
    totalPages: number;
  }>> {
    return this.get<{
      purchases: Purchase[];
      total: number;
      page: number;
      totalPages: number;
    }>('/user/purchases', { page, limit });
  }

  async getPurchase(purchaseId: string): Promise<ApiResponse<Purchase>> {
    return this.get<Purchase>(`/user/purchases/${purchaseId}`);
  }

  // Download history
  async getDownloadHistory(page: number = 1, limit: number = 20): Promise<ApiResponse<{
    downloads: Array<{
      id: string;
      productId: string;
      productName: string;
      downloadDate: string;
      downloadUrl: string;
      expiresAt: string;
    }>;
    total: number;
    page: number;
    totalPages: number;
  }>> {
    return this.get<{
      downloads: Array<{
        id: string;
        productId: string;
        productName: string;
        downloadDate: string;
        downloadUrl: string;
        expiresAt: string;
      }>;
      total: number;
      page: number;
      totalPages: number;
    }>('/user/downloads', { page, limit });
  }

  // Account management
  async deleteAccount(password: string): Promise<ApiResponse<void>> {
    const response = await this.delete<void>('/user/account');
    if (response.success) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
    }
    return response;
  }

  async exportUserData(): Promise<ApiResponse<{ downloadUrl: string; expiresAt: string }>> {
    return this.post<{ downloadUrl: string; expiresAt: string }>('/user/export-data');
  }

  // Notifications
  async getNotifications(page: number = 1, limit: number = 20): Promise<ApiResponse<{
    notifications: Array<{
      id: string;
      type: string;
      title: string;
      message: string;
      read: boolean;
      createdAt: string;
      data?: any;
    }>;
    total: number;
    unreadCount: number;
  }>> {
    return this.get<{
      notifications: Array<{
        id: string;
        type: string;
        title: string;
        message: string;
        read: boolean;
        createdAt: string;
        data?: any;
      }>;
      total: number;
      unreadCount: number;
    }>('/user/notifications', { page, limit });
  }

  async markNotificationAsRead(notificationId: string): Promise<ApiResponse<void>> {
    return this.patch<void>(`/user/notifications/${notificationId}/read`);
  }

  async markAllNotificationsAsRead(): Promise<ApiResponse<void>> {
    return this.patch<void>('/user/notifications/read-all');
  }

  async deleteNotification(notificationId: string): Promise<ApiResponse<void>> {
    return this.delete<void>(`/user/notifications/${notificationId}`);
  }

  // Utility methods
  isAuthenticated(): boolean {
    return !!localStorage.getItem('authToken');
  }

  getAuthToken(): string | null {
    return localStorage.getItem('authToken');
  }

  clearAuth(): void {
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
  }
}

// Export singleton instance
export const userService = new UserService(); 